import React, { useContext, useEffect, useState } from "react";
import { Messages, MessageType } from "../../constants/Messages";
import { createInitialInventory } from "../../services/MaterialAPI";
import InputMethodTabs from "../shared/InputMethodTabs";
import SharedAlertBanner from "../shared/SharedAlertBanner";
import {
    catchError,
    formatUniqueKeyCombination,
    handleInventoryResponseAlerts,
    noDataValidation,
    normalizeString,
    renderErrorDetails,
} from "../../utils/AlertUtils";
import { loadMaterialMappings } from "../../utils/MaterialsUtils";
import { UserContext } from "../../context/UserContext";
import { FIELD_MAPPINGS } from "../../constants/FiledMappings";
import { BULK_TABS } from "../../constants/TabTypes";
import FormComponent from "../generic/FormComponent";

const REQUIRED_FIELDS: string[] = [
    FIELD_MAPPINGS.MATERIAL_TYPE,
    FIELD_MAPPINGS.SIZE,
    FIELD_MAPPINGS.QUANTITY_UNIT,
    FIELD_MAPPINGS.COLOR_NAME,
];

const OPTIONAL_FIELDS: string[] = [
    FIELD_MAPPINGS.COLOR_CODE,
    FIELD_MAPPINGS.MATERIAL_DESCRIPTION,
    FIELD_MAPPINGS.QUANTITY,
    FIELD_MAPPINGS.UNIT_PRICE,
];

const BulkAddInventoryForm: React.FC = () => {
    const [ rawData, setRawData ] = useState<any[]>([]);
    const [ parsedData, setParsedData ] = useState<any[]>([]);
    const [ apiData, setApiData ] = useState<any[]>([]);
    const [ invalidRows, setInvalidRows ] = useState<string[]>([]);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ activeTab, setActiveTab ] = useState<(typeof BULK_TABS)[keyof typeof BULK_TABS]>(BULK_TABS.FILE_UPLOAD);
    const [ materialMappings, setMaterialMappings ] = useState<{
        materialTypeMap: Record<string, number>;
        materialSizeMap: Record<string, number>;
    }>({ materialTypeMap: {}, materialSizeMap: {} });

    const [ fieldMappings, setFieldMappings ] = useState<Record<string, string>>(
        Object.values(FIELD_MAPPINGS).reduce((acc, field) => ({ ...acc, [field]: "" }), {}),
    );

    const [ successAlert, setSuccessAlert ] = useState<{
        message: string;
        type?: MessageType;
        title?: string;
        isHtml?: boolean;
    } | null>(null);

    const [ errorAlert, setErrorAlert ] = useState<{
        message: string;
        type?: MessageType;
        title?: string;
        isHtml?: boolean;
    } | null>(null);

    const { isLoggedIn } = useContext(UserContext);

    useEffect(() => {
        loadMaterialMappings(setMaterialMappings, setErrorAlert);
    }, [ isLoggedIn ]);

    useEffect(() => {
        if (rawData.length > 0) {
            setErrorAlert(null);
            validateAndSetData(rawData);
        }
    }, [ fieldMappings, rawData ]);

    const validateAndSetData = (data: any[]) => {
        const validApiRows: any[] = [];
        const validTableRows: any[] = [];
        const invalidRowsList: string[] = [];
        const seen = new Set();

        data.forEach((row) => {
            try {
                const parsedRow: Record<string, any> = row;
                const materialTypeId = materialMappings.materialTypeMap[normalizeString(parsedRow[FIELD_MAPPINGS.MATERIAL_TYPE])];
                const materialSizeId = materialMappings.materialSizeMap[normalizeString(parsedRow[FIELD_MAPPINGS.SIZE])];

                if (!materialTypeId) throw new Error(Messages.ERROR_INVALID_MATERIAL_TYPE);
                if (!materialSizeId) throw new Error(Messages.ERROR_INVALID_MATERIAL_SIZE);
                if (!parsedRow[FIELD_MAPPINGS.QUANTITY_UNIT]) throw new Error(Messages.ERROR_INVALID_QUANTITY_UNIT);

                const apiData = {
                    material_type_id: materialTypeId,
                    material_size_id: materialSizeId,
                    color_code: parsedRow[FIELD_MAPPINGS.COLOR_CODE]?.trim() || "",
                    color_name: parsedRow[FIELD_MAPPINGS.COLOR_NAME]?.trim() || "",
                    material_description: parsedRow[FIELD_MAPPINGS.MATERIAL_DESCRIPTION]?.trim() || "",
                    quantity: parsedRow[FIELD_MAPPINGS.QUANTITY] ? parseFloat(parsedRow[FIELD_MAPPINGS.QUANTITY]) : undefined,
                    quantity_unit: parsedRow[FIELD_MAPPINGS.QUANTITY_UNIT]?.trim() || "",
                    average_price_per_unit: parsedRow[FIELD_MAPPINGS.UNIT_PRICE] ? parseFloat(parsedRow[FIELD_MAPPINGS.UNIT_PRICE]) : undefined,
                };

                const key = formatUniqueKeyCombination(apiData);
                if (seen.has(key)) {
                    throw new Error(Messages.ERROR_DUPLICATE_ENTRY);
                }
                seen.add(key);

                validApiRows.push(apiData);
                validTableRows.push(parsedRow);
            } catch (error: any) {
                invalidRowsList.push(
                    JSON.stringify({
                        ...row,
                        Reason: error?.message || Messages.UNKNOWN_ERROR,
                    }),
                );
            }
        });

        setApiData(validApiRows);
        setParsedData(validTableRows);
        setInvalidRows(invalidRowsList);

        renderErrorDetails(invalidRowsList, setErrorAlert);
    };

    const handleDataParsed = (data: any[]) => {
        setRawData(data);
        setParsedData(data);
    };

    const handleFieldMappingChange = (updatedMappings: Record<string, string>) => {
        setFieldMappings(updatedMappings);
    };

    const handleSubmit = async () => {
        noDataValidation(parsedData, setErrorAlert);
        setIsLoading(true);

        try {
            const response = await createInitialInventory(apiData);
            handleInventoryResponseAlerts(response, setSuccessAlert, setErrorAlert);
            setParsedData([]);
        } catch (error: any) {
            catchError(error, setErrorAlert);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <FormComponent>
            <SharedAlertBanner
                successAlert={successAlert}
                errorAlert={errorAlert}
                onCloseSuccess={() => setSuccessAlert(null)}
                onCloseError={() => setErrorAlert(null)}
            />
            <InputMethodTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                onDataParsed={handleDataParsed}
                onSubmit={handleSubmit}
                isLoading={isLoading}
                parsedData={parsedData}
                fieldMappings={fieldMappings}
                onFieldMappingChange={handleFieldMappingChange}
                requiredFields={REQUIRED_FIELDS as string[]}
                optionalFields={OPTIONAL_FIELDS as string[]}
            />
        </FormComponent>
    );
};

export default BulkAddInventoryForm;
